// 2026 schedule
// Running an enrollment special - max price $500 (so any horse 3 or older that would be over $500 is just $500)
// January 25 - 2/1
// February 8 - 15
// April 5 - 12
// May 19- 31
// June 7-14
// July 26- Aug 2nd

export const PRICE_CONSTANTS = {
  PREMIUM_3_PLUS: 2000,      // > 3 years old
  STANDARD_3: 1000,          // = 3 years old
  STANDARD_2: 500,           // = 2 years old
  STANDARD_1: 400,           // 1 year old (after March 17)
  BASE_FEE: 250,             // base/min price
  PROMO_CAP: 500             // price cap
};

export const AGE_CONSTANTS = {
  FOAL_AGE_3_PLUS: 3,        // 3+ years old (premium pricing)
  FOAL_AGE_3: 3,             // 3 years old
  FOAL_AGE_2: 2,             // 2 years old
  FOAL_AGE_1: 1,             // 1 year old
  FOAL_AGE_0: 0,             // Yearling (0 years old)
  SIRE_ENROLLED: 0           // Threshold for enrolled sire (sireAge > 0)
};

const dateStartUTC = (year, month, day) => {
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
};

const dateEndUTC = (year, month, day) => {
  return new Date(Date.UTC(year, month - 1, day, 23, 59, 59));
};

// this can be migrated into a database at some point
export const promotions = [
  {
    id: 'promo-2026-jan',
    name: 'January 2026 Special',
    startDate: dateStartUTC(2026, 1, 25),
    endDate: dateEndUTC(2026, 2, 1),
    condition: (foalAge, basePrice) => foalAge >= AGE_CONSTANTS.FOAL_AGE_3_PLUS && basePrice > PRICE_CONSTANTS.PROMO_CAP,
    priceOverride: (basePrice) => Math.min(basePrice, PRICE_CONSTANTS.PROMO_CAP),
    message: '$500 for 3 and older through Feb 1!'
  },
  {
    id: 'promo-2026-feb',
    name: 'February 2026 Special',
    startDate: dateStartUTC(2026, 2, 8),
    endDate: dateEndUTC(2026, 2, 15),
    condition: (foalAge, basePrice) => foalAge >= AGE_CONSTANTS.FOAL_AGE_3_PLUS && basePrice > PRICE_CONSTANTS.PROMO_CAP,
    priceOverride: (basePrice) => Math.min(basePrice, PRICE_CONSTANTS.PROMO_CAP),
    message: '$500 for 3 and older through Feb 15!'
  },
  {
    id: 'promo-2026-apr',
    name: 'April 2026 Special',
    startDate: dateStartUTC(2026, 4, 5),
    endDate: dateEndUTC(2026, 4, 12),
    condition: (foalAge, basePrice) => foalAge >= AGE_CONSTANTS.FOAL_AGE_3_PLUS && basePrice > PRICE_CONSTANTS.PROMO_CAP,
    priceOverride: (basePrice) => Math.min(basePrice, PRICE_CONSTANTS.PROMO_CAP),
    message: '$500 for 3 and older through Apr 12!'
  },
  {
    id: 'promo-2026-may',
    name: 'May 2026 Special',
    startDate: dateStartUTC(2026, 5, 19),
    endDate: dateEndUTC(2026, 5, 31),
    condition: (foalAge, basePrice) => foalAge >= AGE_CONSTANTS.FOAL_AGE_3_PLUS && basePrice > PRICE_CONSTANTS.PROMO_CAP,
    priceOverride: (basePrice) => Math.min(basePrice, PRICE_CONSTANTS.PROMO_CAP),
    message: '$500 for 3 and older through May 31!'
  },
  {
    id: 'promo-2026-jun',
    name: 'June 2026 Special',
    startDate: dateStartUTC(2026, 6, 7),
    endDate: dateEndUTC(2026, 6, 14),
    condition: (foalAge, basePrice) => foalAge >= AGE_CONSTANTS.FOAL_AGE_3_PLUS && basePrice > PRICE_CONSTANTS.PROMO_CAP,
    priceOverride: (basePrice) => Math.min(basePrice, PRICE_CONSTANTS.PROMO_CAP),
    message: '$500 for 3 and older through Jun 14!'
  },
  {
    id: 'promo-2026-jul',
    name: 'July-August 2026 Special',
    startDate: dateStartUTC(2026, 7, 26),
    endDate: dateEndUTC(2026, 8, 2),
    condition: (foalAge, basePrice) => foalAge >= AGE_CONSTANTS.FOAL_AGE_3_PLUS && basePrice > PRICE_CONSTANTS.PROMO_CAP,
    priceOverride: (basePrice) => Math.min(basePrice, PRICE_CONSTANTS.PROMO_CAP),
    message: '$500 for 3 and older through Aug 2!'
  }
];

export function isPromotionActive(promotion, currentDate = new Date()) {
  const now = new Date(currentDate.getTime());
  const start = new Date(promotion.startDate);
  const end = new Date(promotion.endDate);
  
  return now >= start && now <= end;
}

/**
 * Merge configurable tier prices (from the settings API, in `foalPricing`
 * shape) over the built-in PRICE_CONSTANTS. Always returns a complete set, so
 * a missing/partial config falls back to the current hardcoded values.
 */
export function resolvePrices(foalPricing) {
  const p = foalPricing || {};
  return {
    PREMIUM_3_PLUS: Number.isFinite(Number(p.premium3Plus)) ? Number(p.premium3Plus) : PRICE_CONSTANTS.PREMIUM_3_PLUS,
    STANDARD_3: Number.isFinite(Number(p.standard3)) ? Number(p.standard3) : PRICE_CONSTANTS.STANDARD_3,
    STANDARD_2: Number.isFinite(Number(p.standard2)) ? Number(p.standard2) : PRICE_CONSTANTS.STANDARD_2,
    STANDARD_1: Number.isFinite(Number(p.standard1)) ? Number(p.standard1) : PRICE_CONSTANTS.STANDARD_1,
    BASE_FEE: Number.isFinite(Number(p.baseFee)) ? Number(p.baseFee) : PRICE_CONSTANTS.BASE_FEE,
    PROMO_CAP: Number.isFinite(Number(p.promoCap)) ? Number(p.promoCap) : PRICE_CONSTANTS.PROMO_CAP,
  };
}

/**
 * Convert configurable promotions (settings shape: month/day windows,
 * minFoalAge, cap, message, enabled) into runtime promo objects for `year`.
 * Disabled promos are dropped. Returns null only when the input is not a usable
 * array (e.g. settings failed to load) so callers fall back to the built-in
 * `promotions` schedule; a valid but empty array is respected as "no promos".
 */
export function buildPromotions(configList, year = new Date().getFullYear()) {
  if (!Array.isArray(configList)) return null;
  return configList
    .filter((p) => p && p.enabled !== false)
    .map((p, i) => {
      const cap = Number.isFinite(Number(p.cap)) ? Number(p.cap) : PRICE_CONSTANTS.PROMO_CAP;
      const minAge = Number.isFinite(Number(p.minFoalAge)) ? Number(p.minFoalAge) : AGE_CONSTANTS.FOAL_AGE_3_PLUS;
      return {
        id: p.id || `promo-${i}`,
        name: p.name,
        startDate: dateStartUTC(year, p.startMonth, p.startDay),
        endDate: dateEndUTC(year, p.endMonth, p.endDay),
        condition: (foalAge, basePrice) => foalAge >= minAge && basePrice > cap,
        priceOverride: (basePrice) => Math.min(basePrice, cap),
        message: p.message,
      };
    });
}

export function applyPromotions(basePrice, foalAge, currentDate = new Date(), promoList = null) {
  let finalPrice = basePrice;

  const source = Array.isArray(promoList) ? promoList : promotions;
  const activePromotions = source.filter((promo) =>
    isPromotionActive(promo, currentDate)
  );
  
  for (const promotion of activePromotions) {
    if (promotion.condition(foalAge, basePrice)) {
      finalPrice = promotion.priceOverride(basePrice);
      break; // Only one promotion can be applied
    }
  }
  
  return finalPrice;
}

export function getPromotionMessage(currentDate = new Date(), promoList = null) {
  const source = Array.isArray(promoList) ? promoList : promotions;
  const activePromotions = source.filter((promo) =>
    isPromotionActive(promo, currentDate)
  );
  
  if (activePromotions.length > 0) {
    return activePromotions[0].message;
  }
  
  return null;
}
